import React, { useEffect, useState, useRef } from 'react';

interface Props {
  onComplete: (score: number) => void;
}

type PartKey =
  | 'feet'
  | 'calves'
  | 'thighs'
  | 'hands'
  | 'forearms'
  | 'upperArms'
  | 'shoulders'
  | 'neck'
  | 'face'
  | 'stomach';

const SEQUENCE: { key: PartKey; label: string }[] = [
  { key: 'feet', label: 'כפות רגליים' },
  { key: 'calves', label: 'שוקיים' },
  { key: 'thighs', label: 'ירכיים' },
  { key: 'stomach', label: 'בטן' },
  { key: 'hands', label: 'אגרופים / כפות ידיים' },
  { key: 'forearms', label: 'אמה' },
  { key: 'upperArms', label: 'זרוע עליונה' },
  { key: 'shoulders', label: 'כתפיים' },
  { key: 'neck', label: 'צוואר' },
  { key: 'face', label: 'פנים (לסת, מצח)' },
];

export default function BodyFocusedRelaxation({ onComplete }: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [phase, setPhase] = useState<'idle' | 'contract' | 'relax'>('idle');
  const [timeLeft, setTimeLeft] = useState(0);
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const startExercise = () => {
    setActiveIndex(0);
    startPhase('contract', 7);
  };

  const startPhase = (p: 'contract' | 'relax', duration: number) => {
    setPhase(p);
    setTimeLeft(duration);

    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = window.setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
        return t - 1;
      });
    }, 1000);

    // After duration seconds, move to next phase/part
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      if (p === 'contract') {
        // move to relax for same part
        startPhase('relax', 20);
      } else {
        // finished relax -> next part
        setActiveIndex((idx) => {
          if (idx === null) return null;
          const next = idx + 1;
          if (next >= SEQUENCE.length) {
            // done
            setPhase('idle');
            setActiveIndex(null);
            onComplete(30);
            return null;
          }
          // start next contraction
          startPhase('contract', 7);
          return next;
        });
      }
    }, duration * 1000);
  };

  const cancel = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveIndex(null);
    setPhase('idle');
    setTimeLeft(0);
  };

  const activePart = activeIndex !== null ? SEQUENCE[activeIndex] : null;

  return (
    <div className="flex flex-col items-center justify-center h-[70vh] text-foreground">
      {activeIndex === null ? (
        <div className="flex flex-col items-center">
          <div className="w-44 h-44 rounded-full border-4 border-rose-400/30 flex items-center justify-center mb-6">
            <div className="text-3xl font-bold">הרפיית שרירים</div>
          </div>

          <h2 className="text-2xl font-bold mb-2">הרפיה ממוקדת גוף (PMR)</h2>
          <p className="text-muted-foreground text-center max-w-md mb-6">
            תרגיל זה עוזר לזהות ולשחרר מתח בשרירים. עבור כל חלק תתבקשו לכווץ למשך 7 שניות ואז להרפות למשך 20 שניות.
          </p>

          <div className="flex gap-4">
            <button
              onClick={startExercise}
              className="bg-rose-500 hover:bg-rose-400 text-white font-bold py-2 px-6 rounded-lg shadow-md"
            >
              התחל תרגול
            </button>
            <button
              onClick={() => onComplete(0)}
              className="bg-muted hover:bg-muted/70 text-foreground font-bold py-2 px-6 rounded-lg shadow-md"
            >
              חזור
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-6">
          <div className="flex items-start gap-6">
            <div className="w-64 h-80 bg-card/20 rounded-md p-2 flex items-center justify-center">
              {/* Simple SVG human front silhouette with parts that can be highlighted */}
              <svg width="140" height="320" viewBox="0 0 120 280" xmlns="http://www.w3.org/2000/svg">
                {/* feet */}
                <rect x="30" y="250" width="20" height="10" rx="3" fill={activePart?.key === 'feet' ? '#ff5c5c' : '#ddd'} />
                <rect x="70" y="250" width="20" height="10" rx="3" fill={activePart?.key === 'feet' ? '#ff5c5c' : '#ddd'} />
                {/* calves */}
                <rect x="34" y="220" width="18" height="35" rx="6" fill={activePart?.key === 'calves' ? '#ff5c5c' : '#ddd'} />
                <rect x="68" y="220" width="18" height="35" rx="6" fill={activePart?.key === 'calves' ? '#ff5c5c' : '#ddd'} />
                {/* thighs */}
                <rect x="28" y="180" width="24" height="45" rx="8" fill={activePart?.key === 'thighs' ? '#ff5c5c' : '#ddd'} />
                <rect x="68" y="180" width="24" height="45" rx="8" fill={activePart?.key === 'thighs' ? '#ff5c5c' : '#ddd'} />
                {/* stomach */}
                <rect x="40" y="130" width="40" height="50" rx="10" fill={activePart?.key === 'stomach' ? '#ff5c5c' : '#ddd'} />
                {/* arms */}
                <rect x="8" y="120" width="18" height="70" rx="8" fill={activePart?.key === 'upperArms' || activePart?.key === 'forearms' || activePart?.key === 'hands' || activePart?.key === 'shoulders' ? (activePart?.key === 'upperArms' || activePart?.key === 'shoulders' ? '#ff5c5c' : activePart?.key === 'forearms' || activePart?.key === 'hands' ? '#ff5c5c' : '#ddd') : '#ddd'} transform="rotate(0 17 155)" />
                <rect x="94" y="120" width="18" height="70" rx="8" fill={activePart?.key === 'upperArms' || activePart?.key === 'forearms' || activePart?.key === 'hands' || activePart?.key === 'shoulders' ? (activePart?.key === 'upperArms' || activePart?.key === 'shoulders' ? '#ff5c5c' : activePart?.key === 'forearms' || activePart?.key === 'hands' ? '#ff5c5c' : '#ddd') : '#ddd'} transform="rotate(0 103 155)" />
                {/* shoulders */}
                <rect x="30" y="115" width="60" height="12" rx="6" fill={activePart?.key === 'shoulders' ? '#ff5c5c' : '#ddd'} />
                {/* neck */}
                <rect x="52" y="100" width="16" height="18" rx="4" fill={activePart?.key === 'neck' ? '#ff5c5c' : '#ddd'} />
                {/* head */}
                <ellipse cx="60" cy="72" rx="20" ry="22" fill={activePart?.key === 'face' ? '#ff5c5c' : '#ddd'} />
              </svg>
            </div>

            <div className="flex flex-col items-start">
              <h3 className="text-xl font-bold">עכשיו עובדים על: {activePart?.label}</h3>
              <p className="text-muted-foreground">שלב: {phase === 'contract' ? 'כווץ' : 'הרפה'}</p>
              <p className="text-muted-foreground mt-2">שניות שנותרו: {timeLeft > 0 ? timeLeft : 0}</p>

              <div className="mt-4 flex gap-3">
                <button
                  onClick={() => {
                    // Skip to next (simulate finishing relax immediately)
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    // If currently contracting, go to relax; if relaxing go to next part
                    if (phase === 'contract') {
                      startPhase('relax', 20);
                    } else {
                      // advance index manually
                      setActiveIndex((idx) => {
                        if (idx === null) return null;
                        const next = idx + 1;
                        if (next >= SEQUENCE.length) {
                          setPhase('idle');
                          setActiveIndex(null);
                          onComplete(30);
                          return null;
                        }
                        // start next contraction
                        startPhase('contract', 7);
                        return next;
                      });
                    }
                  }}
                  className="bg-rose-500 hover:bg-rose-400 text-white font-bold py-2 px-4 rounded-lg"
                >
                  דילוג / סיום שלב
                </button>

                <button
                  onClick={cancel}
                  className="bg-muted hover:bg-muted/70 text-foreground font-bold py-2 px-4 rounded-lg"
                >
                  עצור
                </button>
              </div>
            </div>
          </div>

          <div className="w-full max-w-xl mt-4">
            <p className="text-muted-foreground text-sm">הנחיות קצרות:</p>
            <ol className="list-decimal list-inside text-muted-foreground mt-2">
              <li>כווצו את קבוצת השרירים למשך 7 שניות (או עד שתרגישו מתיחה).</li>
              <li>שחררו בבת אחת והירגעו למשך 20 שניות, שימו לב להבדל בתחושה.</li>
              <li>המשך לחלק הבא כשהמנחה יבקש.</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
